import { useEffect, useMemo, useState } from "react";
import { collection, getDocs, onSnapshot, orderBy, query } from "firebase/firestore";
import { DATABASE } from "../Config/firebase";
import {
  computeSegmentTotals,
  getMonthlyGraphClientProfit,
  getMonthlyGraphDate,
} from "../Utils/fundsPeriodTotals";

const useMonthlyGraphPeriodTotals = ({ startDate, endDate, usersMap: externalUsersMap }) => {
  const [records, setRecords] = useState([]);
  const [internalUsersMap, setInternalUsersMap] = useState({});
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);

  const hasExternalUsers = externalUsersMap && Object.keys(externalUsersMap).length > 0;
  const usersMap = hasExternalUsers ? externalUsersMap : internalUsersMap;

  useEffect(() => {
    if (hasExternalUsers) {
      setLoadingUsers(false);
      return undefined;
    }

    let cancelled = false;
    setLoadingUsers(true);
    getDocs(collection(DATABASE, "users"))
      .then((snap) => {
        if (cancelled) return;
        const map = {};
        snap.docs.forEach((d) => {
          map[d.id] = { doc_id: d.id, ...d.data() };
        });
        setInternalUsersMap(map);
      })
      .catch((err) => console.error("useMonthlyGraphPeriodTotals: failed to load users", err))
      .finally(() => {
        if (!cancelled) setLoadingUsers(false);
      });

    return () => {
      cancelled = true;
    };
  }, [hasExternalUsers]);

  useEffect(() => {
    const q = query(collection(DATABASE, "monthly_grpahs"), orderBy("graph_month_date", "desc"));
    setLoadingRecords(true);
    const unsub = onSnapshot(
      q,
      (snap) => {
        setRecords(snap.docs.map((d) => ({ id: d.id, doc_id: d.id, ...d.data() })));
        setLoadingRecords(false);
      },
      (err) => {
        console.error("useMonthlyGraphPeriodTotals: snapshot error", err);
        setLoadingRecords(false);
      }
    );
    return unsub;
  }, []);

  const totals = useMemo(() => {
    if (!startDate || !endDate) {
      return {
        individual: { total: 0, count: 0 },
        corporate: { total: 0, count: 0 },
      };
    }

    return computeSegmentTotals(records, usersMap, {
      startDate,
      endDate,
      getAmount: getMonthlyGraphClientProfit,
      getDate: getMonthlyGraphDate,
    });
  }, [records, usersMap, startDate, endDate]);

  return {
    totals,
    loading: loadingRecords || loadingUsers,
  };
};

export default useMonthlyGraphPeriodTotals;
